import { useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";
import { IoCloseOutline, IoFlashOutline, IoSettingsOutline } from "react-icons/io5";
import { FiClock } from "react-icons/fi";
import { TbLayoutDashboard } from "react-icons/tb";
import { HiOutlineMenu } from "react-icons/hi";
import { urlsPages } from "../urls/urls-pages";
import {
    DrawerBackdrop,
    DrawerCloseButton,
    DrawerDivider,
    DrawerFooter,
    DrawerHeader,
    DrawerNavLink,
    DrawerPanel,
    DrawerSectionLabel,
    DrawerSettingsButton,
    DrawerNavBlock,
    MenuIconButton,
    DrawerFooterInfos,
} from "./style";
import { Logo, UserAvatar } from "..";
import { BiExit } from "react-icons/bi";
import { StorageService } from "../storage.service";

function treinosHrefFromPathname(pathname: string): string {
    const m = pathname.match(/^\/treinos\/([^/]+)/);
    if (!m) return urlsPages.drills;
    if (pathname.includes("/acoes")) return urlsPages.drills;
    return urlsPages.workouts.replace(":drillId", m[1]);
}

type NavigationDrawerProps = {
    open: boolean;
    onClose: () => void;
};

export const NavigationDrawer: React.FC<NavigationDrawerProps> = ({ open, onClose }) => {
    const { pathname } = useLocation();

    const treinosTo = useMemo(() => treinosHrefFromPathname(pathname), [pathname]);

    const { isHome, isTreinos, isSessoes } = useMemo(() => {
        const home = pathname === urlsPages.drills;
        const treinos = pathname.startsWith("/treinos");
        const sessoes = pathname === urlsPages.sessionsList;
        return { isHome: home, isTreinos: treinos, isSessoes: sessoes };
    }, [pathname]);

    useEffect(() => {
        if (!open) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = prev;
        };
    }, [open]);

    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [open, onClose]);

    if (!open || typeof document === "undefined") return null;

    return createPortal(
        <>
            <DrawerBackdrop
                type="button"
                aria-label="Fechar menu"
                onClick={onClose}
            />
            <DrawerPanel
                id="app-navigation-drawer"
                role="dialog"
                aria-modal="true"
                aria-label="Menu de navegação"
            >
                <DrawerHeader>
                    <Logo />
                    <DrawerCloseButton
                        type="button"
                        aria-label="Fechar"
                        onClick={onClose}
                    >
                        <IoCloseOutline size={24} />
                    </DrawerCloseButton>
                </DrawerHeader>
                <DrawerDivider />
                <DrawerNavBlock>
                    <DrawerSectionLabel>Principal</DrawerSectionLabel>
                    <DrawerNavLink
                        to={urlsPages.drills}
                        $active={isHome}
                        onClick={onClose}
                    >
                        <TbLayoutDashboard size={20} />
                        Home
                    </DrawerNavLink>
                    <DrawerNavLink to={treinosTo} $active={isTreinos} onClick={onClose}>
                        <IoFlashOutline size={20} />
                        Treinos
                    </DrawerNavLink>
                    <DrawerNavLink
                        to={urlsPages.sessionsList}
                        $active={isSessoes}
                        onClick={onClose}
                    >
                        <FiClock size={20} />
                        Sessões
                    </DrawerNavLink>
                </DrawerNavBlock>
                <DrawerFooter>
                    <DrawerDivider />
                    <DrawerSectionLabel>Sistema</DrawerSectionLabel>

                    <DrawerSettingsButton
                        type="button"
                        onClick={async () => {
                            onClose();
                            await StorageService.logout();
                            window.location.href = urlsPages.signin;
                        }}
                    >
                        <BiExit size={20} />
                        Sair
                    </DrawerSettingsButton>                    

                </DrawerFooter>
            </DrawerPanel>
        </>,
        document.body
    );
};

type MenuOpenButtonProps = {
    onOpen: () => void;
    menuOpen: boolean;
};

export const MenuOpenButton: React.FC<MenuOpenButtonProps> = ({ onOpen, menuOpen }) => (
    <MenuIconButton
        type="button"
        aria-label="Abrir menu de navegação"
        aria-expanded={menuOpen}
        aria-controls="app-navigation-drawer"
        onClick={onOpen}
    >
        <HiOutlineMenu size={24} />
    </MenuIconButton>
);
